import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    CalendarDaysIcon,
    ChatBubbleBottomCenterTextIcon,
    CreditCardIcon,
    ShieldExclamationIcon,
    WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getOperationalAnalytics } from "../api/analyticsApi";
import type { AnalyticsDateRange } from "../types";

const fmtNumber = (value: number | null | undefined) =>
    value == null ? "—" : new Intl.NumberFormat("en-NG").format(value);

const fmtCurrency = (amount: number | null | undefined) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
    }).format(amount);
};

const fmtHours = (value: number | null | undefined) => {
    if (value == null || !Number.isFinite(value)) return "—";
    return `${value.toFixed(1)} hrs`;
};

const StatCard = ({
    label,
    value,
    helper,
}: {
    label: string;
    value: string;
    helper?: string;
}) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
        <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
        {helper ? <p className="mt-1 text-xs text-slate-400">{helper}</p> : null}
    </div>
);

export const OperationalAnalyticsRoute = () => {
    const [dateRange, setDateRange] = useState<AnalyticsDateRange>({});

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["analytics-operational", dateRange],
        queryFn: () => getOperationalAnalytics(dateRange),
    });

    const stats = data?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
                    Failed to load operational analytics
                </p>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
                <p className="text-sm text-slate-500 dark:text-slate-400">No operational data available.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                    <input
                        type="date"
                        value={dateRange.startDate ?? ""}
                        onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value || undefined }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <span className="text-sm text-slate-400">to</span>
                    <input
                        type="date"
                        value={dateRange.endDate ?? ""}
                        onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value || undefined }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
                    Support Workload
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard label="Support Tickets" value={fmtNumber(stats.supportTickets)} />
                    <StatCard label="Open Support Tickets" value={fmtNumber(stats.openSupportTickets)} />
                    <StatCard label="Resolved Support Tickets" value={fmtNumber(stats.resolvedSupportTickets)} />
                    <StatCard
                        label="Resolution Time"
                        value={fmtHours(stats.averageResolutionTimeHours)}
                        helper="Average resolution speed"
                    />
                </div>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <CreditCardIcon className="h-4 w-4" />
                        Payments
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <StatCard label="Payment Failures" value={fmtNumber(stats.paymentFailures)} />
                        <StatCard label="Failed Payment Amount" value={fmtCurrency(stats.paymentFailureAmount)} />
                    </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <WrenchScrewdriverIcon className="h-4 w-4" />
                        System Health
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <StatCard label="App Errors" value={fmtNumber(stats.appErrors)} />
                        <StatCard label="Open Operational Alerts" value={fmtNumber(stats.openOperationalAlerts)} />
                        <StatCard label="Webhook Failures" value={fmtNumber(stats.webhookFailures)} />
                        <StatCard label="Gateway Dispatch Failures" value={fmtNumber(stats.gatewayDispatchFailures)} />
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <ShieldExclamationIcon className="h-4 w-4" />
                    Alerts Triggered
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                    <StatCard
                        label="Operational Alerts Triggered"
                        value={fmtNumber(stats.operationalAlertsTriggered)}
                        helper="Alerts whose latest trigger happened in the period"
                    />
                    <StatCard
                        label="Open Operational Alerts"
                        value={fmtNumber(stats.openOperationalAlerts)}
                        helper="Currently unresolved incidents"
                    />
                </div>
            </div>
        </div>
    );
};
